import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import {
  ContentSection, FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd,
} from "@/components/age-calculator/shared";
import {
  TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate,
} from "@/components/age-calculator/premium";
import {
  AnchorToc, AnchorSection, PresetChips, LifeProgressBar, WeekdayMilestoneGrid,
  buildAppFaqLd, chronoDiff, CopyResultButton,
} from "@/components/age-calculator/sub-shell";

const TITLE = "UPSC Age Calculator 2026: Exact Age as on 1 August (CSE Eligibility)";
const DESC = "Compute your UPSC Civil Services age as on the 1st August cut-off with category-wise eligibility (General 21-32, OBC +3, SC/ST +5, PwBD +10) and remaining attempts.";

const FAQS = [
  { q: "What is the UPSC age limit for 2026?", a: "For UPSC CSE 2026, the candidate must be 21-32 years as on 1 August 2026 (General). OBC gets +3 years, SC/ST +5 years and PwBD up to +10 years relaxation." },
  { q: "How is UPSC age calculated exactly?", a: "UPSC uses chronological age — years, months and days between your DOB and 1st August of the exam year. Even one day over 32 makes a General candidate ineligible." },
  { q: "How many attempts do I have for UPSC CSE?", a: "General: 6 attempts, OBC: 9 attempts, SC/ST: unlimited till upper age limit, PwBD (General/OBC): 9 attempts." },
  { q: "Can I appear for UPSC after 32?", a: "Only reserved-category candidates can cross 32 as per official relaxations. General candidates who cross the upper limit as on 1 August of the exam year become permanently ineligible." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "category-limits", label: "Category-wise Limits" },
  { id: "attempts", label: "Attempts Left" },
  { id: "relaxations", label: "Age Relaxations" },
  { id: "faqs", label: "FAQs" },
];

const CUT_OFF_YEARS = [2025, 2026, 2027, 2028];

export const Route = createFileRoute("/age-calculator/upsc-eligibility")({
  head: () => leafHead({
    path: "/age-calculator/upsc-eligibility",
    title: TITLE,
    description: DESC,
    scripts: [
      buildAppFaqLd({ name: "UPSC Age Eligibility Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "UPSC Eligibility", url: "https://drhint.com/age-calculator/upsc-eligibility" },
      ]),
    ],
  }),
  component: Page,
});

type Category = "General" | "OBC" | "SC/ST" | "PwBD";
const REL: Record<Category, { max: number; attempts: string }> = {
  General: { max: 32, attempts: "6" },
  OBC: { max: 35, attempts: "9" },
  "SC/ST": { max: 37, attempts: "Unlimited (till upper age)" },
  PwBD: { max: 42, attempts: "9" },
};

function Page() {
  const [dob, setDob] = useState("");
  const [cutYear, setCutYear] = useState(2026);
  const [cat, setCat] = useState<Category>("General");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const cutoff = `${cutYear}-08-01`;

  const r = useMemo(() => (dob ? chronoDiff(dob, cutoff) : null), [dob, cutoff]);
  const eligible = r ? r.years >= 21 && r.years < REL[cat].max : false;

  const copyText = r
    ? `UPSC Age as on 01-Aug-${cutYear}: ${r.years}y ${r.months}m ${r.days}d — Category ${cat} — ${eligible ? "ELIGIBLE" : "NOT ELIGIBLE"}`
    : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / UPSC Eligibility
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Cross-check your <strong>UPSC CSE 2026 age eligibility</strong> in seconds. Enter DOB, pick category and the exam year — this tool computes your exact age <em>as on 1 August</em>, the day UPSC freezes the cut-off. For general date maths use the{" "}
          <Link to="/age-calculator" className="text-primary underline">main Age Calculator</Link>.
        </p>
      </section>

      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
              <PresetChips
                label="Common DOBs"
                presets={[
                  { label: "Born 1995", value: "1995-06-15" },
                  { label: "Born 2000", value: "2000-06-15" },
                  { label: "Born 2003", value: "2003-06-15" },
                ]}
                onPick={(v) => { setDob(v); reset(); }}
              />
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Exam Year (Cut-off = 1 Aug {cutYear})</div>
              <div className="flex flex-wrap gap-1.5">
                {CUT_OFF_YEARS.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => { setCutYear(y); reset(); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${cutYear === y ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-muted/70"}`}
                  >{y}</button>
                ))}
              </div>
              <div className="text-sm font-semibold mt-4 mb-2">Category</div>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(REL) as Category[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setCat(c); reset(); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${cat === c ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-muted/70"}`}
                  >{c}</button>
                ))}
              </div>
            </div>
          </div>

          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Check UPSC Eligibility" />
          <AnalyzingOverlay visible={analyzing} />

          <ReservedResultsArea minHeight={260}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label={`Age on 01-Aug-${cutYear}`} value={`${r.years}y ${r.months}m ${r.days}d`} />
                  <ResultCard label="Category" value={cat} sub={`Upper limit ${REL[cat].max} yrs`} />
                  <ResultCard label="Attempts Allowed" value={REL[cat].attempts} />
                  <ResultCard
                    label="Status"
                    value={eligible ? "ELIGIBLE ✅" : "NOT ELIGIBLE ❌"}
                    sub={eligible ? `${REL[cat].max - r.years} yrs of eligibility left` : "Age outside 21–upper limit band"}
                  />
                </div>
                <div className="mt-4 flex justify-center">
                  <CopyResultButton text={copyText} label="Copy Eligibility Status" />
                </div>
                <LifeProgressBar dob={dob} />
                <WeekdayMilestoneGrid dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">
                Enter DOB, pick year + category, then tap <span className="font-semibold text-primary">Check UPSC Eligibility</span>.
              </div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="category-limits" title="UPSC Category-wise Age Limits (Fresh 2026)">
        <ul>
          <li><strong>General / EWS:</strong> 21 to 32 years — hard ceiling on 1 Aug 2026.</li>
          <li><strong>OBC (non-creamy):</strong> 21 to 35 years — 3-year relaxation.</li>
          <li><strong>SC / ST:</strong> 21 to 37 years — 5-year relaxation.</li>
          <li><strong>PwBD (General/EWS):</strong> up to 42 years — 10-year relaxation.</li>
          <li><strong>Ex-servicemen:</strong> add 5 years to your category limit after adjusting military service.</li>
        </ul>
      </AnchorSection>

      <AnchorSection id="attempts" title="Attempts Left — How UPSC Counts Them">
        <p>An attempt is counted only when you actually appear in a paper of the Preliminary exam. Filling the form and skipping does not count. Appearing in Prelims but not Mains still counts as one attempt.</p>
        <ul>
          <li>General: <strong>6 attempts</strong> total.</li>
          <li>OBC: <strong>9 attempts</strong>.</li>
          <li>SC/ST: <strong>unlimited</strong> till the upper age limit is crossed.</li>
          <li>PwBD (Gen/OBC): <strong>9 attempts</strong>. PwBD (SC/ST): unlimited.</li>
        </ul>
      </AnchorSection>

      <AnchorSection id="relaxations" title="Extra Relaxations Most Aspirants Forget">
        <p>The <strong>upper-age relaxation</strong> is cumulative — a PwBD OBC candidate gets both the 3 OBC years <em>and</em> the 10 PwBD years on top of the General 32-year ceiling. Central Government Civil Servants with 3+ years of continuous service and personnel from Jammu &amp; Kashmir domicile (during 1980-89) also enjoy additional relaxation windows. Always cross-check with the current-year UPSC notification PDF before assuming.</p>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}